import { useState } from "react";
import { Check, Loader2, Lock, Film, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { http, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/components/Paywall";
import { useResetCountdown } from "@/components/MessageTools";
import { AccessDetails } from "@/components/Access";
import { CheckoutSummary } from "@/components/CheckoutSummary";
import { usePlanConfig, PlanTable, PassTable, videoFact, TIER_NAMES } from "@/components/PlanInfo";
import { TIERS, PASSES, PLAN_DETAILS } from "@/lib/pricing";

const RANK = { free: 0, starter: 1, plus: 2, unlimited: 3 };
const CARD = "rounded-2xl border border-white/10 bg-[#121725] p-5";

function useCheckout() {
  const [busy, setBusy] = useState("");
  const go = async (key, path = "/payments/checkout") => {
    setBusy(key);
    try {
      const { data } = await http.post(path, { lookup_key: key, origin_url: window.location.origin });
      window.location.href = data.checkout_url || data.url;
    } catch (e) { toast.error(formatError(e)); setBusy(""); }
  };
  return { busy, go };
}

function tierLabel(t, sub, current) {
  if (current) return "Current plan";
  if (t.id === "free") return sub ? "Cancel in billing" : "Included";
  if (!sub) return `Get ${t.name}`;
  return RANK[t.id] > RANK[sub.tier] ? "Upgrade" : "Switch";
}

const periodSuffix = (t, annual) => (t.id === "free" ? "" : annual ? " / year" : " / month");

function TierCard({ t, annual, ent, busy, onPick }) {
  const sub = ent?.subscription;
  const current = ent && !ent.is_admin && (sub ? sub.tier === t.id && sub.interval === (annual ? "annual" : "monthly") : t.id === "free");
  const key = `${t.id}_${annual ? "annual" : "monthly"}`;
  return (
    <div className={`relative rounded-2xl border p-6 flex flex-col rise ${t.popular ? "border-sky-400/60 bg-sky-500/[0.06]" : "border-white/10 bg-[#121725]"}`} data-testid={`tier-card-${t.id}`}>
      {t.popular && <span className="absolute -top-3 left-6 text-[11px] px-2.5 py-1 rounded-full bg-sky-400 text-white font-medium">Most popular</span>}
      <p className="font-display text-lg font-semibold">{t.name}</p>
      <p className="text-sm text-neutral-400">{t.tag}</p>
      <p className="mt-3"><span className="font-display text-4xl font-semibold">${annual ? t.y : t.m}</span><span className="text-neutral-500 text-sm">{periodSuffix(t, annual)}</span></p>
      <p className="text-xs h-4 mt-1 text-emerald-300">{annual && t.save ? `Save ${t.save} vs monthly ($${(t.y / 12).toFixed(2)}/mo)` : ""}</p>
      <ul className="mt-4 space-y-2 flex-1">
        {t.feats.map((f) => (f.endsWith("plus:")
          ? <li key={f} className="text-sm text-neutral-400">{f}</li>
          : <li key={f} className="text-sm flex gap-2 text-neutral-300"><Check className="h-4 w-4 text-sky-400 shrink-0 mt-0.5" />{f}</li>))}
      </ul>
      <button disabled={current || (t.id === "free" && !sub) || !!busy || ent?.is_admin} onClick={() => onPick(t.id === "free" ? "" : key)} data-testid={`tier-select-${t.id}`}
        className={`mt-6 h-10 rounded-full text-sm font-medium transition-colors disabled:opacity-50 ${t.popular ? "bg-sky-400 text-white hover:bg-sky-500" : "border border-white/15 hover:bg-white/5"}`}>
        {busy === key ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : tierLabel(t, sub, current)}
      </button>
    </div>
  );
}

const DAILY_LABEL = { voice: "Listen and Mic", draw: "Draw it diagrams" };

function DailyUsage({ ent }) {
  const reset = useResetCountdown();
  if (!ent?.daily) return null;
  return (
    <div className={CARD} data-testid="daily-usage">
      <div className="flex justify-between items-baseline"><p className="text-sm font-medium">Today's usage</p><p className="text-xs text-sky-300 font-mono" data-testid="daily-reset-countdown">{reset}</p></div>
      <p className="text-xs text-neutral-500 mb-4">Resets at midnight UTC. Separate from your messaging time.</p>
      <div className="space-y-4">
        {Object.entries(ent.daily).map(([k, d]) => {
          const pct = d.limit ? Math.min(100, Math.round((100 * d.used) / d.limit)) : 0;
          return (
            <div key={k} data-testid={`daily-usage-${k}`}>
              <div className="flex justify-between text-xs"><span className="text-neutral-300">{DAILY_LABEL[k]}</span>
                <span className="text-neutral-500" data-testid={`daily-usage-${k}-text`}>{d.limit ? `${d.used} of ${d.limit} used · ${d.left} left` : "Not on your plan"}</span></div>
              <div className="h-1 rounded-full bg-white/5 mt-2 overflow-hidden"><div className={`h-full ${d.left > 0 ? "bg-sky-400" : "bg-yellow-300"} transition-[width] duration-500`} style={{ width: `${pct}%` }} /></div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const FEATURE_LABEL = { practice: "Practice, mock exams and flashcards", progress: "Progress tracking", groups: "Create groups, share materials, host games", web: "Web research with sources", draw: "Draw it diagrams" };

function PlanFeatures({ ent, cfg }) {
  return (
    <div className={CARD} data-testid="plan-features">
      <p className="text-sm font-medium mb-3 flex items-center gap-2"><Sparkles className="h-4 w-4 text-sky-300" />What's on your plan now</p>
      <ul className="space-y-2">
        {Object.entries(FEATURE_LABEL).map(([k, label]) => (
          <li key={k} className={`text-sm flex gap-2 ${ent.features[k] ? "text-neutral-200" : "text-neutral-500"}`} data-testid={`plan-feature-${k}`}>
            {ent.features[k] ? <Check className="h-4 w-4 text-sky-300 shrink-0 mt-0.5" /> : <Lock className="h-4 w-4 shrink-0 mt-0.5" />}{label}
            {!ent.features[k] && cfg && <span className="text-xs">· {TIER_NAMES[cfg.features[k]]} and up</span>}
          </li>
        ))}
        {cfg && <li className="text-sm flex gap-2 text-neutral-200" data-testid="plan-feature-video"><Film className="h-4 w-4 text-sky-300 shrink-0 mt-0.5" />{videoFact(cfg, ent.tier)}</li>}
      </ul>
    </div>
  );
}

function renewalText(sub) {
  if (!sub) return "";
  if (sub.cancel_at_period_end) return `Doesn't renew · access ends ${fmtDate(sub.current_period_end)}`;
  if (sub.scheduled_tier) return `Switches to ${TIER_NAMES[sub.scheduled_tier] || sub.scheduled_tier} on ${fmtDate(sub.current_period_end)}`;
  return `Renews ${fmtDate(sub.current_period_end)}`;
}

function CurrentPlan({ ent, bought, onManage, busy }) {
  if (ent.is_admin) return <p className="text-sm text-neutral-400" data-testid="current-plan">Admin account · unlimited access, no limits.</p>;
  const sub = ent.subscription;
  return (
    <div className={`${CARD} flex flex-wrap items-start justify-between gap-4`} data-testid="current-plan">
      <div className="min-w-0 flex-1 space-y-3">
        <p className="font-display text-lg">You're on <span className="text-sky-300">{TIER_NAMES[ent.base_tier]}</span>{sub ? ` (${sub.interval})` : ""}</p>
        {sub && <p className="text-xs text-neutral-500" data-testid="current-plan-renewal">{renewalText(sub)}</p>}
        {bought
          ? <AccessDetails ent={ent} />
          : <p className="text-xs text-neutral-500" data-testid="current-plan-usage-note">Get a plan or a pass to see how much you've used and how much is left here.</p>}
      </div>
      {sub && (
        <button onClick={onManage} disabled={!!busy} className="text-sm px-4 py-2 rounded-full border border-white/15 hover:bg-white/5" data-testid="manage-billing-btn">
          {busy === "portal" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Payment methods and invoices"}
        </button>
      )}
    </div>
  );
}

function Passes({ ent, busy, onPick }) {
  return (
    <div id="day-passes">
      <h2 className="font-display text-xl font-medium">Passes</h2>
      <p className="text-sm text-neutral-500 mt-1 mb-4">One timed window each, no subscription. The clock starts with your first message.</p>
      <div className="grid sm:grid-cols-3 gap-3">
        {PASSES.map((p) => (
          <div key={p.key} className="rounded-xl border border-white/10 bg-white/[0.03] p-4 flex items-center justify-between gap-3" data-testid={`day-pass-${p.key}`}>
            <div><p className="text-sm font-medium">{p.name}</p><p className="text-xs text-neutral-500">{p.desc}</p></div>
            <button onClick={() => onPick(p.key)} disabled={!!busy || ent?.is_admin} data-testid={`buy-${p.key}`} className="px-4 py-2 rounded-full bg-white/10 text-sm hover:bg-white/15 disabled:opacity-50 shrink-0">
              {busy === p.key ? <Loader2 className="h-4 w-4 animate-spin" /> : `$${p.price}`}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function IntervalToggle({ annual, setAnnual }) {
  return (
    <div className="flex rounded-full border border-white/10 p-1" data-testid="billing-interval-toggle">
      {["Monthly", "Annual"].map((l, i) => (
        <button key={l} onClick={() => setAnnual(!!i)} data-testid={`interval-${l.toLowerCase()}`}
          className={`px-4 py-1.5 rounded-full text-sm transition-colors ${annual === !!i ? "bg-white/10 text-white" : "text-neutral-400"}`}>{l}</button>
      ))}
    </div>
  );
}

export default function BillingPage() {
  const { ent } = useAuth();
  const cfg = usePlanConfig();
  const [annual, setAnnual] = useState(false);
  const [pick, setPick] = useState(null);
  const { busy, go } = useCheckout();
  const sub = ent?.subscription;
  // Live usage only shows once a plan or an unused pass has been bought.
  const bought = !!sub || (ent?.passes || []).some((p) => p.state !== "expired");
  const direct = (key) => (sub && !key.endsWith("_day") ? go(key, "/billing/portal") : go(key));
  const choose = (key) => {
    if (!key) go("portal", "/billing/portal");
    else if (cfg) setPick(key);
    else direct(key);
  };
  const confirm = () => direct(pick);
  return (
    <div className="flex-1 overflow-y-auto scroll-thin">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10 space-y-10" data-testid="billing-page">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight">Billing</h1>
          <p className="text-neutral-400 mt-3">Your plan, what you've used, and your options.</p>
        </div>
        {ent && (
          <section className="grid lg:grid-cols-[1.2fr_1fr] gap-4" data-testid="usage-dashboard">
            <CurrentPlan ent={ent} bought={bought} onManage={() => go("portal", "/billing/portal")} busy={busy} />
            {!ent.is_admin && <PlanFeatures ent={ent} cfg={cfg} />}
            {!ent.is_admin && bought && <DailyUsage ent={ent} />}
          </section>
        )}
        <section className="space-y-4">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <h2 className="font-display text-xl font-medium">Plans</h2>
            <IntervalToggle annual={annual} setAnnual={setAnnual} />
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {TIERS.map((t) => <TierCard key={t.id} t={t} annual={annual} ent={ent} busy={busy} onPick={choose} />)}
          </div>
        </section>
        <Passes ent={ent} busy={busy} onPick={choose} />
        <section className="space-y-4" data-testid="billing-plan-details">
          <h2 className="font-display text-xl font-medium">Plan details</h2>
          <PlanTable cfg={cfg} testid="billing-plan-table" />
          <PassTable cfg={cfg} />
          <ul className="text-xs text-neutral-400 space-y-1.5 list-disc pl-4" data-testid="plan-details-note">{PLAN_DETAILS.map((d) => <li key={d}>{d}</li>)}</ul>
          <p className="text-xs text-neutral-500">Upgrades apply right away; downgrades and cancellations apply at the end of your paid period. Payment methods, invoices and payment history open on Stripe's secure billing page.</p>
        </section>
      </div>
      <CheckoutSummary cfg={cfg} pick={pick} sub={sub} busy={busy} onConfirm={confirm} onClose={() => setPick(null)} />
    </div>
  );
}
